import type { PrintReceiptRaster, PrintReceiptRasterUpload } from '@gc/shared';

import { toBase64 } from '@/features/settings/printLogo';
import { api } from '@/lib/api';

/**
 * A drawn receipt on its way to `POST /print-jobs/:id/raster` — deflated when
 * the browser can (2026-09-28).
 *
 * Receipts are drawn up to 16,384 rows now, and at 72 bytes a row the tallest
 * is 1.2 MB of packed rows, 1.5 MB as base64, nearly all of it white paper.
 * The browser's own `CompressionStream('deflate')` takes that to a few percent
 * with no new dependency, and the server inflates it with node's zlib. Where
 * there is no CompressionStream, or it fails, the upload is exactly the
 * uncompressed shape it always was — the server takes both, and a failed
 * compression must never cost the customer the drawn receipt.
 */

/** Bytes in, zlib-wrapped deflate (RFC 1950) out — what the server inflates. */
export type Deflate = (bytes: Uint8Array) => Promise<Uint8Array>;

/** The browser's deflate, or null where it has none. */
export function browserDeflate(): Deflate | null {
  if (typeof CompressionStream !== 'function') return null;
  return async (bytes) => {
    // 'deflate' in the Compression Streams spec IS the zlib format — header,
    // raw deflate, Adler-32 — which is what `inflateSync` on the server reads.
    // ('deflate-raw' would not be.)
    const stream = new Blob([bytes as Uint8Array<ArrayBuffer>])
      .stream()
      .pipeThrough(new CompressionStream('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  };
}

/** Base64 back to bytes — the inverse of `toBase64`. */
export function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let at = 0; at < binary.length; at += 1) bytes[at] = binary.charCodeAt(at);
  return bytes;
}

/**
 * The body to upload for `raster`: deflated when that is smaller, otherwise
 * the raster untouched.
 *
 * Drawing stays synchronous and hands over today's shape; compression is a
 * separate last step so every caller — checkout, the café, the undrawn poll —
 * gets it by calling {@link uploadRaster} instead of posting directly.
 *
 * The compressed bytes go under `deflated` and the body carries NO `data`
 * (2026-09-28). If the API is ever rolled back to a build that knows only the
 * uncompressed shape while this tab stays open, that build must refuse the
 * upload — then the job stays queued and the typed receipt prints after the
 * grace. Compressed bytes in `data` would pass its check, be stored as if they
 * were rows, and throw at every dispatch: no paper at all.
 */
export async function rasterForUpload(
  raster: PrintReceiptRaster,
  deflate: Deflate | null = browserDeflate(),
): Promise<PrintReceiptRasterUpload> {
  if (!deflate) return raster;
  try {
    const packed = fromBase64(raster.data);
    const compressed = await deflate(packed);
    // Never larger than what it replaces: rows of noise do not compress, and
    // the uncompressed shape is always accepted.
    if (compressed.length === 0 || compressed.length >= packed.length) return raster;
    return {
      widthDots: raster.widthDots,
      heightDots: raster.heightDots,
      compression: 'deflate',
      deflated: toBase64(compressed),
    };
  } catch {
    return raster;
  }
}

/** Offer a drawn image to the job already queued with its typed body. */
export async function uploadRaster(jobId: string, raster: PrintReceiptRaster): Promise<void> {
  await api.post(`/print-jobs/${jobId}/raster`, await rasterForUpload(raster));
}
